import axios from 'axios';
import { Raw, Render, Api } from '@fixtuur/backpack';

/**
 * Fetch the json document associated with a given model / particle id
 * @param id the particle id used to fetch the json document
 * @param modelsUrl the service url used to fetch the json document
 * @returns AttachmentPoints: raw attachment points document json
 */
const retrieveAttachmentsDocument = async (
    particle: Api.Configuration.Particle.Particle,
    serviceConfig: Api.ServiceConfig,
): Promise<Render.Particle.AttachmentPoints.AttachmentPoints> => {
    const attachmentsUrl = Api.Configuration.Particle.getAttachmentsUrl(serviceConfig, particle);

    try {
        const attachments = (
            await axios.get(attachmentsUrl, {
                responseType: 'json',
            })
        ).data as Render.Particle.AttachmentPoints.AttachmentPoints;

        return attachments;
    } catch {
        throw new Error(
            `Failed to fetch attachments data for particle ${Api.Configuration.Particle.id(
                particle,
            )}`,
        );
    }
};

/**
 * Construct a raw tree structure which lists each slot and the attached objects within it,
 * where the model url and position is set at the correct level in the tree for rendering.
 * @param particles combined model url and raw attachments document
 * @param attachmentTag the tag to look up - as this is called recursively, it should be initiated with the tag of the root particle.
 * @param position the world location for this object - as this is called recursively, it should be initiated with the position of the root.
 * @param orientation the quaternion orientation of the this object - as this is called recursively, it should be initiated with the orientation of the root.
 * @returns ParticleTreeNodeWithSlots: Raw tree structure
 */
const deserialiseAttachmentsRecursive = (
    particles: Render.Particle.Tree.RawParticleData[],
    attachmentTag: string,
    position: Raw.Position3D.Position3D = { x: 0, y: 0, z: 0 },
    orientation: Raw.Orientation.Orientation3D = { w: 1, x: 0, y: 0, z: 0 },
): Render.Particle.Tree.ParticleTreeNodeWithSlots => {
    const currentParticle = particles.find(attachment => attachment.document.tag === attachmentTag);
    if (!currentParticle) {
        throw new Error(`failed to construct attachments tree, tag ${attachmentTag} not found`);
    }

    const particleId = currentParticle.id;
    const originDelta = currentParticle.document.originDelta;
    const slots = currentParticle.document.slots
        .map(slot => {
            const name = slot.name;
            const applicableTags = slot.applicableTags;
            const isOptional = !slot.onlyAttachmentPointOptionForApplicableTags;
            const attached = particles
                .filter(particle => slot.applicableTags.includes(particle.document.tag))
                .flatMap(particle => {
                    return slot.attachmentPoints.map(ap => {
                        return deserialiseAttachmentsRecursive(
                            particles,
                            particle.document.tag,
                            ap.position,
                            ap.orientation,
                        );
                    });
                });
            return {
                name,
                isOptional,
                attached,
                applicableTags,
            };
        })
        .filter(slot => slot.attached.length);

    return {
        id: particleId,
        attachmentTag,
        position,
        orientation,
        originDelta,
        slots,
    };
};

/**
 * Given a raw tree structure, collapse all slots down to options for the front-end by the previously generated `isOptional` bool.
 * Any options which, in reality, are only one choice after collapsing down are moved to the required set of attachments so the UI
 *  does not have to deal with it.
 * @param treeNode the root node to recurse
 * @param particles the original particle data used to ensure slots are actually applicable (they're in the flat set of applicable tags)
 * @param index used to uniquely identify attachment options in the event that there are multiple elements (e.g. two basins each with tap locations).
 *     This index is incremented and appended to the key identifier (e.g. `mixer/2`).
 */
const collapseTreeOptionsRecursive = (
    serviceConfig: Api.ServiceConfig,
    treeNode: Render.Particle.Tree.ParticleTreeNodeWithSlots,
    particles: Render.Particle.Tree.RawParticleData[],
    index: number = 0,
): Render.Particle.Tree.ParticleTreeNodeWithAttachments => {
    const { id, attachmentTag, position, orientation, originDelta, slots } = treeNode;
    const modelUrl = Api.Configuration.Particle.getModelUrl(
        serviceConfig,
        Api.Configuration.Particle.construct(id),
    );

    // get required attachments
    const required = slots
        .filter(slot => !slot.isOptional)
        .map(slot => {
            return {
                name: slot.name,
                attached: slot.attached.map((a, i) =>
                    collapseTreeOptionsRecursive(serviceConfig, a, particles, i),
                ),
            };
        });

    // get options
    const options: { [tag: string]: Render.Particle.Tree.ParticleAttachment[] } = {};
    const optionalSlots = slots.filter(slot => slot.isOptional);
    const optionalNames = optionalSlots
        .map(slot => slot.name)
        .filter((value, index, self) => self.indexOf(value) === index);

    optionalNames.forEach(name => {
        const slot = optionalSlots.find(slot => slot.name === name);
        if (slot) {
            slot.applicableTags
                .filter(tag => particles.some(p => p.document.tag === tag))
                .forEach(tag => {
                    if (!options[`${tag}/${index}`]) options[`${tag}/${index}`] = [];
                    options[`${tag}/${index}`].push({
                        name: slot.name,
                        attached: slot.attached.map((a, i) =>
                            collapseTreeOptionsRecursive(serviceConfig, a, particles, i),
                        ),
                    });
                });
        }
    });

    // remove any "options" which are only one choice (e.g. large handles)
    Object.keys(options).forEach(tagIndex => {
        if (options[tagIndex].length === 1) {
            required.push(...options[tagIndex]);
            delete options[tagIndex];
        }
    });

    const attachments = {
        required,
        options: options,
    };

    return {
        id,
        attachmentTag,
        modelUrl,
        position,
        orientation,
        originDelta,
        attachments,
    };
};

/**
 * Given an array of raw particles, find the root tag to deserialise attachments with.
 * @throws Error if found tags != 1
 * @param particles array to search
 * @returns Tag
 */
function findRootTag(particles: Render.Particle.Tree.RawParticleData[]): string {
    const root = particles
        // Find the particle which has no matching attachment in any other particles' applicableTags array
        .filter(
            source =>
                !particles.find(search =>
                    search.document.slots.some(slot =>
                        slot.applicableTags.includes(source.document.tag),
                    ),
                ),
        )
        // Remove edge-case tag for invalid / not-found attachments
        .filter(p => p.document.tag !== 'none');

    if (root.length !== 1) {
        throw new Error(
            `Invalid furniture structure, ${root.length} root tags found: ${
                root.length && '(' + root.map(r => r.document.tag).join(', ') + ')'
            } for particles: ${particles.map(p => p.id).join(', ')}`,
        );
    }

    return root[0].document.tag;
}

/**
 * Given a list of particles, resolve the attachments into a simplified tree structure for both furniture rendering and UI attachment options.
 *
 * Will make the actual API calls to fetch attachments.
 *
 * Returns a root node for the furniture tree structure
 */
export async function ResolveFurniture(
    particles: Api.Configuration.Particle.Particle[],
    serviceConfig: Api.ServiceConfig,
): Promise<Render.Particle.Tree.ParticleTreeNodeWithAttachments> {
    const particlesWithAttachments: Render.Particle.Tree.RawParticleData[] = await Promise.all(
        particles.map(async particle => ({
            id: Api.Configuration.Particle.id(particle),
            document: await retrieveAttachmentsDocument(particle, serviceConfig),
        })),
    );

    const rootTag = findRootTag(particlesWithAttachments);
    const particleTreeRaw = deserialiseAttachmentsRecursive(particlesWithAttachments, rootTag);

    return collapseTreeOptionsRecursive(serviceConfig, particleTreeRaw, particlesWithAttachments);
}

/**
 * Given a tree node, return a list of options relating to location of attachment points.
 * @param tree a tree node to parse
 * @param userOptions used to recurse the structure and build up options before returning
 * @returns a flat array of options to choose
 */
export function getUserOptions(
    tree: Render.Particle.Tree.ParticleTreeNodeWithAttachments,
): Render.Particle.Tree.TagAttachmentOptions[] {
    const options = Object.entries(tree.attachments.options).map(([tag, attachments]) => {
        return {
            tag,
            options: attachments.map(a => a.name),
            selected: attachments[0].name,
        };
    });

    const userOptions = options;

    const allChildren = Object.values(tree.attachments.options)
        .concat(tree.attachments.required)
        .flat();

    userOptions.push(
        ...allChildren.flatMap(child => child.attached.flatMap(a => getUserOptions(a))),
    );

    return userOptions;
}

export function refineParticleTreeNode(
    treeNode: Render.Particle.Tree.ParticleTreeNodeWithAttachments,
    userOptions: { [key: string]: string },
): Render.Particle.Tree.ParticleTreeNode {
    const otherOptions: { [tag: string]: Render.Particle.Tree.ParticleAttachment[] } = {};

    const active = treeNode.attachments.required
        .flatMap(requiredAttachments =>
            requiredAttachments.attached.map(attached =>
                refineParticleTreeNode(attached, userOptions),
            ),
        )
        .concat(
            Object.entries(treeNode.attachments.options).flatMap(([key, val]) =>
                val.flatMap(v => {
                    if (userOptions[key] === v.name) {
                        return v.attached.map(attached =>
                            refineParticleTreeNode(attached, userOptions),
                        );
                    } else {
                        otherOptions[key] = [...(otherOptions[key] ?? []), v];
                        return [];
                    }
                }),
            ),
        );

    return { ...treeNode, attachments: { active, options: otherOptions } };
}

export function getParticleIdsInTree(tree: Render.Particle.Tree.ParticleTreeNode): Array<string> {
    return [tree.id, ...tree.attachments.active.flatMap(getParticleIdsInTree)];
}
